"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { getSupabaseClient } from '@/lib/supabaseClient';
import { apiFetchJson } from '@/lib/api';
import { User } from '@supabase/supabase-js';

interface UserContextType {
  user: User | null;
  coinBalance: number;
  isLoading: boolean;
  refreshBalance: () => Promise<void>;
  canAfford: (cost: number) => boolean;
  isAuthenticated: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [coinBalance, setCoinBalance] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);

  const fetchBalanceAsync = useCallback(async () => {
    try {
      const response = await apiFetchJson<{ balance: number }>('/user/balance');
      setCoinBalance(response.balance);
    } catch (error) {
      console.error('Error fetching coin balance:', error);
      // Set balance to 0 if API fails - don't block the app
      setCoinBalance(0);
    }
  }, []);

  const refreshBalance = useCallback(async () => {
    if (!user) return;
    await fetchBalanceAsync();
  }, [user, fetchBalanceAsync]);

  const canAfford = useCallback((cost: number) => {
    return coinBalance >= cost;
  }, [coinBalance]);

  const initializeUser = useCallback(async () => {
    const supabase = getSupabaseClient();
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        // Fetch initial balance separately - don't block auth on this
        fetchBalanceAsync();
      }
    } catch (error) {
      console.error('Error initializing user:', error);
    } finally {
      setIsLoading(false);
    }
  }, [fetchBalanceAsync]);

  useEffect(() => {
    initializeUser();
  }, [initializeUser]);

  useEffect(() => {
    const supabase = getSupabaseClient();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          setUser(session.user);
          // Fetch balance for newly signed in user - don't block auth
          fetchBalanceAsync();
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setCoinBalance(0);
        }
        setIsLoading(false);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchBalanceAsync]);

  const value: UserContextType = {
    user,
    coinBalance,
    isLoading,
    refreshBalance,
    canAfford,
    isAuthenticated: !!user,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
